import { API } from '@/src/api/api'

import { queryKeys } from '@/src/api/queryKeys'
import type { MeterControllerGetMetersData, MeterControllerGetMetersResponse, MeterResponseDto } from '@/src/api/types/generated-types'
import { type EnrichedDevice, type EnrichedLocation, useLocationsContext } from '@/src/contexts/LocationsContext'
import { getLocationAndBuildingNames } from '@/src/features/Messstellen/utils/messstellen.utils'
import { ONE_MINUTE_IN_MS } from '@/src/utils/constants'
import { useQuery } from '@tanstack/react-query'
import _ from 'lodash'
import { useCallback } from 'react'

export type Meter = MeterResponseDto & {
  displayTitleWithLocationAndBuilding: string
  color: { bg: string; stroke: string }
  name: string
  granularity: string
  locationAndBuildingNames: string
}

// Only the query params the generated client actually sends (meterControllerGetMeters in
// services.gen.ts). Callers spread hierarchy-selection objects that carry extra fields
// (companyFirebaseId, locationFirebaseId) which never reach the wire — keying on them would
// give byte-identical requests different cache entries, so they fire twice.
const toMetersRequestParams = (data: MeterControllerGetMetersData): MeterControllerGetMetersData => ({
  companyId: data.companyId,
  buildingId: data.buildingId,
  locationId: data.locationId,
  regionId: data.regionId,
  deviceIds: data.deviceIds,
  includeTimeframeTotalConsumption: data.includeTimeframeTotalConsumption,
  includeYtdTotalConsumption: data.includeYtdTotalConsumption,
  includeYtdTotalConsumptionUntilEndOfMonth: data.includeYtdTotalConsumptionUntilEndOfMonth,
  from: data.from,
  to: data.to,
  includeFullSeries: data.includeFullSeries,
  granularity: data.granularity,
  includeTimeframePeakloadAndBaseload: data.includeTimeframePeakloadAndBaseload,
  includeYtdPeakloadAndBaseload: data.includeYtdPeakloadAndBaseload,
})

// Single source of truth for GET /meters caching: every consumer (useMeters, useAllMeters,
// SeriesContext's prefetch) must derive key + fetch from here so identical concurrent
// requests collapse into one.
export const metersQueryOptions = (data: MeterControllerGetMetersData) => {
  const params = toMetersRequestParams(data)
  return {
    queryKey: queryKeys.meters.manyByParams.toKeyWithArgs(params),
    queryFn: async (): Promise<MeterControllerGetMetersResponse> => {
      const response = await API.meters.fetch(params)
      return response
    },
    staleTime: ONE_MINUTE_IN_MS * 5,
  }
}

export const useMeters = (data: MeterControllerGetMetersData, { enabled: enabledQuery }: { enabled: boolean } = { enabled: true }) => {
  const { locations, getDevices, selectedRegionIndex, selectedLocationIndex, selectedBuildingIndex, isInitialized } = useLocationsContext()
  const devices = getDevices(selectedRegionIndex, selectedLocationIndex, selectedBuildingIndex)

  const hasHierarchySelection = !!data.deviceIds?.length || !!data.locationId || !!data.buildingId || !!data.regionId || !!data.companyId
  const enabled = enabledQuery && hasHierarchySelection
  // react-query memoizes `select` by reference identity. An inline arrow is a new
  // reference on every render, so enrichMeters re-ran each time and returned a new array,
  // invalidating every downstream memo and re-rendering the whole table.
  const select = useCallback(
    (meterDtos: MeterControllerGetMetersResponse) => enrichMeters(meterDtos, devices, locations as EnrichedLocation[]),
    [devices, locations]
  )
  const query = useQuery<MeterControllerGetMetersResponse, Error, Meter[]>({
    ...metersQueryOptions(data),
    enabled,
    select,
  })

  return query
}

const enrichMeters = (meterDtos: MeterResponseDto[], devices: EnrichedDevice[], locations?: EnrichedLocation[]): Meter[] => {
  if (!meterDtos?.length || !devices?.length) return []

  const locationsById = _.keyBy(locations, 'locationPgId')
  const devicesById = _.keyBy(devices, 'id')

  const meters = meterDtos.map(meter => {
    const deviceLocation = locationsById[meter.locationId]
    const deviceInfo = devicesById[meter.deviceId]
    const locationAndBuildingNames = getLocationAndBuildingNames(meter, deviceLocation)

    return {
      ...meter,
      color: deviceInfo?.color || { bg: '#000000', stroke: '#000000' },
      name: meter.meterTitle || `Meter ${meter.deviceId}`,
      granularity: deviceInfo?.granularity || '',
      displayTitleWithLocationAndBuilding: `${meter.meterTitle} ${locationAndBuildingNames}`,
      locationAndBuildingNames,
    }
  })

  return meters
}
